import type { PasswordStrength } from '../components/password-strength-indicator/password-strength-indicator'
import { ERROR_MESSAGES } from './errorMessages'

export const MIN_SIZE = 8
const RECOMMENDED_SIZE = 10

export type PasswordValidationError =
  | 'empty'
  | 'tooShort'
  | 'repeatedCharacters'
  | 'sequentialCharacters'
  | 'containsName'
  | 'containsEmail'
  | null

export interface PasswordValidationResult {
  isValid: boolean
  error: PasswordValidationError
  errorMessage?: string
}

const VALIDATION_ERROR_MESSAGES: Record<Exclude<PasswordValidationError, null>, string> = {
  empty: ERROR_MESSAGES.signup.details.password.empty,
  tooShort: 'A senha deve conter ao menos 8 caracteres',
  repeatedCharacters: 'A senha não pode conter caracteres idênticos em sequência, como aaa',
  sequentialCharacters: 'A senha não pode conter sequências de caracteres, como 123 ou abc',
  containsName: 'A senha não pode conter o seu nome',
  containsEmail: 'A senha não pode conter partes do seu e-mail',
}

const MATCHERS = {
  UNIQUE_CHARS: /(.)\1\1+/g,
  SEQUENTIAL_NUMERIC: /(012|123|234|345|456|567|890|098|987|876|765|654|543|210)/g,
  SEQUENTIAL_ALPHA: (() => {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz'
    const sequences: string[] = []
    for (let i = 0; i < alphabet.length - 2; i++) {
      sequences.push(alphabet.substring(i, i + 3))
    }
    return new RegExp(sequences.join('|'), 'gi')
  })(),
  LETTERS: /[a-z]/,
  SPECIAL_CHARS: /[\W_]/,
  NUMBERS: /\d/,
  UPPER_CASE: /[A-Z]/,
}

interface TestParams {
  password: string
  nameLower: string[]
  emailLower: string
  passwordLower: string
  passwordLowerUnique: string
  minLength: number
}

type TestFunction = (params: TestParams) => boolean
type TestsSuite = Record<string, TestFunction>

function hasSequentialNumeric(password: string): boolean {
  return Boolean(password.match(MATCHERS.SEQUENTIAL_NUMERIC))
}

function hasSequentialAlpha(password: string): boolean {
  return Boolean(password.match(MATCHERS.SEQUENTIAL_ALPHA))
}

function hasRepeatedCharactersInternal(password: string): boolean {
  if (!password) return false
  return Boolean(password.toLowerCase().match(MATCHERS.UNIQUE_CHARS))
}

const TESTS = {
  NEGATIVE: {
    neg_namePart: ({ nameLower, passwordLowerUnique }: TestParams) =>
      !!nameLower[0] && nameLower.some((namePart) => passwordLowerUnique.includes(namePart)),
    neg_email: ({ passwordLowerUnique, emailLower }: TestParams) =>
      !!emailLower && passwordLowerUnique.includes(emailLower),
    neg_identical: ({ passwordLower }: TestParams) => hasRepeatedCharactersInternal(passwordLower),
    neg_tooShort: ({ password, minLength }: TestParams) => password.length < minLength,
    neg_sequentialNumeric: ({ password }: TestParams) => hasSequentialNumeric(password),
    neg_sequentialAlpha: ({ password }: TestParams) => hasSequentialAlpha(password),
  },
  NEUTRAL: {
    neu_letters: ({ passwordLowerUnique }: TestParams) =>
      MATCHERS.LETTERS.test(passwordLowerUnique),
    neu_regularSize: ({ password, minLength }: TestParams) =>
      password.length >= minLength && password.length <= RECOMMENDED_SIZE,
  },
  POSITIVE: {
    pos_specialChars: ({ password }: TestParams) => Boolean(password.match(MATCHERS.SPECIAL_CHARS)),
    pos_numbers: ({ password }: TestParams) => Boolean(password.match(MATCHERS.NUMBERS)),
    pos_longSize: ({ password }: TestParams) => password.length > RECOMMENDED_SIZE,
    pos_upper: ({ password }: TestParams) => Boolean(password.match(MATCHERS.UPPER_CASE)),
  },
}

function getSuiteScore(suite: TestsSuite, params: TestParams): number {
  let score = 0
  for (const test of Object.values<TestFunction>(suite)) {
    if (test(params)) {
      score++
    }
  }
  return score
}

const minMax = (min: number, max: number, value: number) => Math.min(Math.max(value, min), max)

function buildParams(
  password: string,
  fullName?: string,
  email?: string,
  minLength = MIN_SIZE
): TestParams {
  const passwordTrimmed = password?.trim() || ''
  return {
    password: passwordTrimmed,
    nameLower: (fullName || '').toLowerCase().split(' ').filter(Boolean),
    emailLower: (email || '').toLowerCase().split('@')[0],
    passwordLower: passwordTrimmed.toLowerCase(),
    passwordLowerUnique: passwordTrimmed.toLowerCase().replace(MATCHERS.UNIQUE_CHARS, '$1'),
    minLength,
  }
}

export function calculatePasswordStrength(
  password: string,
  fullName?: string,
  email?: string,
  minLength = MIN_SIZE
): PasswordStrength {
  if (!password?.trim()) return 'idle'

  const params = buildParams(password, fullName, email, minLength)

  const MAX_SCORE = 3
  const MAX_NEUTRAL = 2
  const MAX_NEGATIVE = 1

  const neutralScore = minMax(0, MAX_NEUTRAL, getSuiteScore(TESTS.NEUTRAL, params))
  const positiveScore = minMax(0, MAX_SCORE, getSuiteScore(TESTS.POSITIVE, params))
  const negativeScore = minMax(0, MAX_NEGATIVE, getSuiteScore(TESTS.NEGATIVE, params))

  const baseScore = minMax(0, MAX_SCORE, neutralScore + positiveScore)
  const finalScore = Math.max(1, baseScore - negativeScore)

  if (finalScore === 1) return 'veryWeak'
  if (finalScore === 2) return 'canImprove'
  return 'strong'
}

/**
 * Validates password rules and returns validation result
 * This is the central validation function used by both form validation and strength calculation
 * @param password - Password to be validated
 * @param minLength - Minimum password length (default: 8)
 * @param fullName - Full name to check if password contains name parts
 * @param email - Email to check if password contains email parts
 * @returns Validation result with error type and message
 */
export function validatePasswordRules(
  password: string,
  minLength = MIN_SIZE,
  fullName?: string,
  email?: string
): PasswordValidationResult {
  if (!password) {
    return {
      isValid: false,
      error: 'empty',
      errorMessage: VALIDATION_ERROR_MESSAGES.empty,
    }
  }

  if (password.length < minLength) {
    return {
      isValid: false,
      error: 'tooShort',
      errorMessage: VALIDATION_ERROR_MESSAGES.tooShort,
    }
  }

  if (hasRepeatedCharacters(password)) {
    return {
      isValid: false,
      error: 'repeatedCharacters',
      errorMessage: VALIDATION_ERROR_MESSAGES.repeatedCharacters,
    }
  }

  if (hasSequentialCharacters(password)) {
    return {
      isValid: false,
      error: 'sequentialCharacters',
      errorMessage: VALIDATION_ERROR_MESSAGES.sequentialCharacters,
    }
  }

  if (fullName) {
    const nameLower = fullName.toLowerCase().split(' ').filter(Boolean)
    const passwordLowerUnique = password.toLowerCase().replace(MATCHERS.UNIQUE_CHARS, '$1')
    if (nameLower[0] && nameLower.some((namePart) => passwordLowerUnique.includes(namePart))) {
      return {
        isValid: false,
        error: 'containsName',
        errorMessage: VALIDATION_ERROR_MESSAGES.containsName,
      }
    }
  }

  if (email) {
    const emailLower = email.toLowerCase().split('@')[0]
    const passwordLowerUnique = password.toLowerCase().replace(MATCHERS.UNIQUE_CHARS, '$1')
    if (emailLower && passwordLowerUnique.includes(emailLower)) {
      return {
        isValid: false,
        error: 'containsEmail',
        errorMessage: VALIDATION_ERROR_MESSAGES.containsEmail,
      }
    }
  }

  return {
    isValid: true,
    error: null,
  }
}

/**
 * Checks if the password contains sequential characters (numeric or alphabetic)
 * @param password - Password to be validated
 * @returns true if contains sequences, false otherwise
 */
export function hasSequentialCharacters(password: string): boolean {
  if (!password) return false
  return hasSequentialNumeric(password) || hasSequentialAlpha(password)
}

/**
 * Checks if the password contains 3 or more identical characters in sequence
 * @param password - Password to be validated
 * @returns true if contains repeated characters, false otherwise
 */
export function hasRepeatedCharacters(password: string): boolean {
  return hasRepeatedCharactersInternal(password)
}
